/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Heading, Text } from 'grommet';
import { FORM_ERROR, SubmissionErrors } from 'final-form';
import { get } from 'lodash';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';

import { useThemeColor as c } from '@blend/alchemy';
import { ActionButton, AlertBar, InputContainer, isEmptyValue } from '@blend/enterprise-components';
import * as Sentry from '@sentry/browser';

import * as RonApi from 'blb-api/ron';
import { Modal } from 'blb-components/GrommetComponents';
import {
  getUserRoleCheckboxConfig,
  groupInputConfig,
} from 'blb-components/AuthenticatedApp/RON/InviteNotaryModal/inputConfigs';
import { emailInputConfig, getNameInputConfig } from 'blb-inputs/common';
import { errorMessage } from 'blb-utils/errors';
import { requiredEmailValidator } from 'blb-utils/validators';

export namespace InviteNotaryModal {
  export interface Props {
    handleClose: () => void;
    isOpen: boolean;
  }
}

const ModalContent = styled(Box)`
  color: ${c('night90')};
  width: 540px;
  max-width: 540px;
`;

const BLEND_GROUP = 'blend';

interface FormValues {
  email: string;
  firstName: string;
  group: string;
  isAdmin: boolean;
  isNotary: boolean;
  lastName: string;
  groupId?: string;
}

interface Option {
  text: string;
  value: string;
}

export const InviteNotaryModal: React.FC<InviteNotaryModal.Props> = ({ isOpen, handleClose }) => {
  const [adminGroups, setAdminGroups] = useState<Option[]>([]);
  const [groupsFetched, setGroupsFetched] = useState<boolean>(false);
  const [isUserClientAdmin, setIsUserClientAdmin] = useState<boolean>(false);
  const [clientAdminGroup, setClientAdminGroup] = useState<Option>({ text: '', value: '' });

  useEffect(() => {
    const fetchAvailableGroups = async () => {
      const groups = await RonApi.getGroupsForUser();
      const options = groups.map(group => ({
        text: group.name,
        value: group.id,
      }));
      // if only one group is found, this means that the user is a client admin
      // the client admin can only assign notaries to their group, so we should disable the group selection dropdown if the user is a client admin
      if (options.length === 1) {
        setIsUserClientAdmin(true);
        setClientAdminGroup(options[0]);
      }
      setAdminGroups(options);
    };

    if (!groupsFetched) {
      void fetchAvailableGroups();
      setGroupsFetched(true);
    }
  });

  const onSubmit = async (formValues: FormValues): Promise<void | SubmissionErrors> => {
    try {
      await RonApi.inviteNotary({
        email: formValues.email,
        firstName: formValues.firstName,
        isAdmin: formValues.isAdmin,
        isNotary: formValues.isNotary,
        lastName: formValues.lastName,
        groupId: isUserClientAdmin ? clientAdminGroup.value : formValues.groupId,
      });
      handleClose();
    } catch (err) {
      Sentry.captureException(err);
      return {
        [FORM_ERROR]: get(err, 'response.data.error.message', errorMessage('INVITE_USER', 'notary')),
      };
    }
  };

  const initialValues = {
    group: BLEND_GROUP,
    isAdmin: false,
    isNotary: true,
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleSubmit, submitError, submitting, values, hasValidationErrors }) => {
        let roleError: string | undefined;
        if (!values.isNotary && !values.isAdmin) {
          roleError = 'Please select at least one role.';
        }
        const submitButton = (
          <ActionButton
            disabled={hasValidationErrors || !!roleError}
            id="it-inviteNotaryModal-invite-btn"
            isLoading={submitting}
            label="Invite"
            onClick={handleSubmit}
          />
        );
        return (
          <Modal buttonComponents={submitButton} handleClose={handleClose} isOpen={isOpen} title="Invite new notary">
            <ModalContent>
              {submitError && (
                <AlertBar id="it-inviteNotaryModal-error" message={submitError as string} theme="error" />
              )}
              <Text margin={{ vertical: 'medium' }} size="medium">
                Enter the details of a notary you want to invite to Notary Workspace.
              </Text>
              <Heading level={5} margin={{ bottom: 'small' }}>
                <Text weight="bold">Notary information</Text>
              </Heading>

              <Field
                {...emailInputConfig}
                customComponentProps={{ disabled: false, type: 'text' }}
                name="email"
                validate={requiredEmailValidator}
              />
              <Field {...getNameInputConfig('first')} validate={isEmptyValue('First name is required')} />
              <Field {...getNameInputConfig('last')} validate={isEmptyValue('Last name is required')} />

              <Field
                {...groupInputConfig(adminGroups, isUserClientAdmin, clientAdminGroup.value)}
                validate={!isUserClientAdmin ? isEmptyValue('Group is required') : undefined}
              />

              <InputContainer
                forceInputError={true}
                input={{ name: 'role' }}
                inputComponent={Box}
                label="Role"
                meta={{ error: roleError }}
              >
                <Box direction="row" gap="small">
                  <Field {...getUserRoleCheckboxConfig('Notary', 'isNotary')} />
                  <Field {...getUserRoleCheckboxConfig('Admin', 'isAdmin')} />
                </Box>
              </InputContainer>
            </ModalContent>
          </Modal>
        );
      }}
    </Form>
  );
};
